import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Model } from '../types/model';
import { ColorCollectionModel } from '../types/colorCollectionModel';
import { modelService } from '../services/modelService';
import '../styles/AddModelsModal.css';

interface AddModelsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (models: Array<{ modelId: string; isUpgrade: boolean; cost: number; price: number }>) => void;
  workspaceId: string;
  projectId: string;
  existingModels?: ColorCollectionModel[];
  editMode?: boolean;
}

interface ModelRow extends Model {
  isUpgrade: boolean;
  cost: number;
  price: number;
  isSelected: boolean;
}

const AddModelsModal: React.FC<AddModelsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  workspaceId,
  projectId,
  existingModels = [],
  editMode = false
}) => {
  const [models, setModels] = useState<ModelRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    modelCollection: '',
    type: '',
    bed: '',
    den: ''
  });
  const [bulkCost, setBulkCost] = useState<string>('0');
  const [bulkPrice, setBulkPrice] = useState<string>('0');

  useEffect(() => {
    if (isOpen && projectId) {
      fetchModels();
    }
  }, [isOpen, projectId]);

  const fetchModels = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await modelService.getAll(workspaceId, {
        projectId: projectId,
        limit: 1000,
        sortBy: 'modelCollection',
        sortOrder: 'asc'
      });

      const allModels = response.models || [];

      // Map models to rows with selection state and pricing
      const modelRows: ModelRow[] = allModels.map((model: Model) => {
        const existingModel = existingModels.find(em => em.modelId === model._id);

        return {
          ...model,
          isUpgrade: existingModel?.isUpgrade || false,
          cost: existingModel?.cost || 0,
          price: existingModel?.price || 0,
          isSelected: editMode ? !!existingModel : false
        };
      });

      // Filter based on mode
      if (editMode) {
        // In edit mode, show only models that are already added
        const filteredRows = modelRows.filter(row => row.isSelected);
        setModels(filteredRows);
      } else {
        // In add mode, exclude models that are already added
        const existingModelIds = new Set(existingModels.map(em => em.modelId));
        const filteredRows = modelRows.filter(row => !existingModelIds.has(row._id));
        setModels(filteredRows);
      }
    } catch (err: any) {
      console.error('Error fetching models:', err);
      setError(err.response?.data?.message || 'Failed to fetch models');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelect = (modelId: string) => {
    setModels(prev =>
      prev.map(model =>
        model._id === modelId
          ? { ...model, isSelected: !model.isSelected }
          : model
      )
    );
  };

  const handleToggleUpgrade = (modelId: string) => {
    setModels(prev =>
      prev.map(model =>
        model._id === modelId
          ? { ...model, isUpgrade: !model.isUpgrade }
          : model
      )
    );
  };

  const handleCostChange = (modelId: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setModels(prev =>
      prev.map(model =>
        model._id === modelId
          ? { ...model, cost: numValue }
          : model
      )
    );
  };

  const handlePriceChange = (modelId: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setModels(prev =>
      prev.map(model =>
        model._id === modelId
          ? { ...model, price: numValue }
          : model
      )
    );
  };

  const handleSave = () => {
    const selectedModels = models
      .filter(model => model.isSelected)
      .map(model => ({
        modelId: model._id,
        isUpgrade: model.isUpgrade,
        cost: model.cost,
        price: model.price
      }));

    if (selectedModels.length === 0) {
      setError('Please select at least one model');
      return;
    }

    onSave(selectedModels);
    handleClose();
  };

  const handleClose = () => {
    setModels([]);
    setSearchTerm('');
    setFilters({ modelCollection: '', type: '', bed: '', den: '' });
    setError('');
    onClose();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilters({ modelCollection: '', type: '', bed: '', den: '' });
  };

  const handleApplyBulkPricing = () => {
    const cost = parseFloat(bulkCost) || 0;
    const price = parseFloat(bulkPrice) || 0;

    setModels(prev =>
      prev.map(model =>
        model.isSelected
          ? { ...model, cost, price }
          : model
      )
    );
  };

  // Get unique filter options
  const collections = Array.from(new Set(models.map(m => m.modelCollection).filter(Boolean)));
  const types = Array.from(new Set(models.map(m => m.type).filter(Boolean)));
  const beds = Array.from(new Set(models.map(m => m.bed).filter(Boolean)));
  const dens = Array.from(new Set(models.map(m => m.den).filter(Boolean)));

  // Filter models based on search and filters
  const filteredModels = models.filter(model => {
    const matchesSearch = !searchTerm ||
      model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.modelCollection?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      model.type?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCollection = !filters.modelCollection || model.modelCollection === filters.modelCollection;
    const matchesType = !filters.type || model.type === filters.type;
    const matchesBed = !filters.bed || String(model.bed) === filters.bed;
    const matchesDen = !filters.den || String(model.den) === filters.den;

    return matchesSearch && matchesCollection && matchesType && matchesBed && matchesDen;
  });

  const selectedCount = models.filter(m => m.isSelected).length;

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="add-models-modal-content">
        <div className="modal-header">
          <h3>{editMode ? 'Edit Models' : 'Add Models'}</h3>
          <button className="modal-close-btn" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        {/* Filters */}
        <div className="add-models-filters">
          <div className="add-models-filters-row">
            <select
              className="form-input"
              value={filters.modelCollection}
              onChange={(e) => setFilters(prev => ({ ...prev, modelCollection: e.target.value }))}
            >
              <option value="">Select Model Collection</option>
              {collections.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>

            <select
              className="form-input"
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
            >
              <option value="">Select Type</option>
              {types.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>

            <select
              className="form-input"
              value={filters.bed}
              onChange={(e) => setFilters(prev => ({ ...prev, bed: e.target.value }))}
            >
              <option value="">Select Beds</option>
              {beds.map(bed => (
                <option key={bed} value={String(bed)}>{bed} Bed{bed !== 1 ? 's' : ''}</option>
              ))}
            </select>

            <select
              className="form-input"
              value={filters.den}
              onChange={(e) => setFilters(prev => ({ ...prev, den: e.target.value }))}
            >
              <option value="">Select Den</option>
              {dens.map(den => (
                <option key={den} value={String(den)}>{den} Den{den !== 1 ? 's' : ''}</option>
              ))}
            </select>
          </div>

          <div className="add-models-filters-row">
            <input
              type="text"
              className="form-input"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ gridColumn: '1 / 3' }}
            />
            <button
              className="btn btn-secondary btn-md"
              onClick={handleClearFilters}
            >
              Clear
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="modal-body">
          {loading ? (
            <div className="add-models-loading">Loading models...</div>
          ) : (
            <div className="add-models-table-wrapper">
              <table className="add-models-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <input
                        type="checkbox"
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setModels(prev => prev.map(m => ({ ...m, isSelected: checked })));
                        }}
                        checked={filteredModels.length > 0 && filteredModels.every(m => m.isSelected)}
                        disabled={editMode}
                      />
                    </th>
                    <th>Model Collection</th>
                    <th>Model Name</th>
                    <th>Type</th>
                    <th>Upgrade</th>
                    <th>Cost</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredModels.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="add-models-empty">
                        {editMode
                          ? 'No models have been added to this color collection yet.'
                          : 'No models found matching your filters.'}
                      </td>
                    </tr>
                  ) : (
                    filteredModels.map(model => (
                      <tr key={model._id} className={model.isSelected ? 'selected' : ''}>
                        <td>
                          <input
                            type="checkbox"
                            checked={model.isSelected}
                            onChange={() => handleToggleSelect(model._id)}
                            disabled={editMode}
                          />
                        </td>
                        <td>{model.modelCollection || '-'}</td>
                        <td>{model.name}</td>
                        <td>{model.type || '-'}</td>
                        <td>
                          <label className="switch">
                            <input
                              type="checkbox"
                              checked={model.isUpgrade}
                              onChange={() => handleToggleUpgrade(model._id)}
                            />
                            <span className="slider"></span>
                          </label>
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-input-sm"
                            value={model.cost}
                            onChange={(e) => handleCostChange(model._id, e.target.value)}
                            min="0"
                            step="0.01"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-input-sm"
                            value={model.price}
                            onChange={(e) => handlePriceChange(model._id, e.target.value)}
                            min="0"
                            step="0.01"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="add-models-footer-info">
            <span>Price: </span>
            <input
              type="number"
              className="form-input-sm"
              value={bulkPrice}
              onChange={(e) => setBulkPrice(e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
          <div className="add-models-footer-info">
            <span>Cost: </span>
            <input
              type="number"
              className="form-input-sm"
              value={bulkCost}
              onChange={(e) => setBulkCost(e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
          <button
            className="btn btn-secondary btn-md"
            onClick={handleApplyBulkPricing}
            disabled={selectedCount === 0}
          >
            Apply
          </button>
          <div style={{ flex: 1 }}></div>
          <button
            className="btn btn-secondary btn-md"
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary btn-md"
            onClick={handleSave}
            disabled={selectedCount === 0 || loading}
          >
            {editMode ? 'Save' : 'Add'} ({selectedCount})
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddModelsModal;
